import React, { useEffect, useState, useCallback } from "react";
import { Plus, Loader2, Trash2, Pencil, User, Flag, Eye, MessageSquare, Send, Paperclip, X, Bell, ChevronDown, ChevronRight, ListChecks, CheckSquare } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import { toast } from "sonner";

const STATUSES = ["Pendiente", "En proceso", "En espera", "Completada"];
const ALL_STATUSES = ["Pendiente", "En proceso", "En espera", "Completada", "Cancelada"];
const ACT_STATUSES = ["Pendiente", "En proceso", "Completada"];
const PRIORITIES = ["Baja", "Media", "Alta", "Urgente"];
const PRIORITY_COLOR = { Baja: "text-muted-foreground", Media: "text-blue-600", Alta: "text-amber-600", Urgente: "text-destructive" };
const STATUS_BADGE = { Pendiente: "bg-slate-200 text-slate-700", "En proceso": "bg-blue-100 text-blue-700", "En espera": "bg-amber-100 text-amber-700", Completada: "bg-emerald-100 text-emerald-700", Cancelada: "bg-red-100 text-red-700" };
const empty = { title: "", description: "", assignee_id: "", priority: "Media", start_date: "", due_date: "", reminder: "", status: "Pendiente", progress: 0, category: "", notes: "", activities: [] };

const fileUrl = (id) => `${process.env.REACT_APP_BACKEND_URL}/api/files/${id}?auth=${localStorage.getItem("selec_token")}`;
const initials = (n) => (n || "?").split(" ").map((x) => x[0]).slice(0, 2).join("").toUpperCase();
const fmtTime = (iso) => { try { return new Date(iso).toLocaleString("es-GT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };

export default function Tasks() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("mine");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [detail, setDetail] = useState(null);

  const isOwner = (t) => t.owner_id === user.id || user.is_admin;

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([api.get("/tasks"), api.get("/tasks/assignable")])
      .then(([t, u]) => { setTasks(t.data); setUsers(u.data); })
      .catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm({ ...empty, assignee_id: user.id, activities: [] }); setOpen(true); };
  const openEdit = (t) => { setEditing(t); setForm({ ...empty, ...t, assignee_id: t.assignee_id || user.id, activities: (t.activities || []).map((a) => ({ id: a.id, title: a.title, status: a.status })) }); setOpen(true); };

  const save = async () => {
    try {
      const payload = { ...form, progress: Number(form.progress) || 0, activities: (form.activities || []).filter((a) => (a.title || "").trim()) };
      if (editing) await api.put(`/tasks/${editing.id}`, payload);
      else await api.post("/tasks", payload);
      toast.success("Tarea guardada"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (t) => { try { await api.delete(`/tasks/${t.id}`); toast.success("Tarea eliminada"); load(); if (detail?.id === t.id) setDetail(null); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const dismiss = async (t) => { try { await api.post(`/tasks/${t.id}/dismiss`); toast.success("Tarea quitada de tu muro"); load(); if (detail?.id === t.id) setDetail(null); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const move = async (t, status) => {
    try {
      const { data } = await api.post(`/tasks/${t.id}/status`, { status });
      setTasks((prev) => prev.map((x) => (x.id === data.id ? data : x)));
      if (detail?.id === data.id) setDetail(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const syncTask = (data) => { setTasks((prev) => prev.map((x) => (x.id === data.id ? data : x))); if (detail?.id === data.id) setDetail(data); };

  const openDetail = async (t) => {
    setDetail(t);
    try { const { data } = await api.post(`/tasks/${t.id}/seen`); setDetail(data); syncTask(data); } catch { /* noop */ }
  };

  const visible = tasks.filter((t) => {
    if (tab === "mine") return t.assignee_id === user.id;
    if (tab === "byme") return t.owner_id === user.id;
    return true;
  });
  const mineCount = tasks.filter((t) => t.assignee_id === user.id).length;
  const mineUnread = tasks.filter((t) => t.assignee_id === user.id && t.unread).length;
  const bymeCount = tasks.filter((t) => t.owner_id === user.id).length;

  const TabBtn = ({ id, label, count, unread }) => (
    <button onClick={() => setTab(id)} data-testid={`tab-${id}`}
      className={`relative px-4 py-2 text-xs font-bold uppercase tracking-wider rounded-sm transition-colors ${tab === id ? "bg-primary text-primary-foreground" : "bg-muted/50 hover:bg-muted text-muted-foreground"}`}>
      {label} <span className="font-mono ml-1 opacity-80">{count}</span>
      {unread > 0 && <span className="absolute -top-1.5 -right-1.5 bg-destructive text-white text-[10px] font-bold rounded-full h-4 min-w-4 px-1 flex items-center justify-center">{unread}</span>}
    </button>
  );

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-muted-foreground">Asigna tareas con actividades (checklist), da seguimiento y conversa dentro de cada una.</p>
        <Button onClick={openCreate} className="rounded-sm gap-2 font-bold uppercase tracking-wider text-xs" data-testid="new-task-btn"><Plus className="h-4 w-4" /> Nueva tarea</Button>
      </div>

      <div className="flex items-center gap-2 flex-wrap" data-testid="task-tabs">
        <TabBtn id="mine" label="Asignadas a mí" count={mineCount} unread={mineUnread} />
        <TabBtn id="byme" label="Yo asigné" count={bymeCount} />
        <TabBtn id="all" label="Todas" count={tasks.length} />
      </div>

      {loading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div> : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="kanban-board">
          {STATUSES.map((st) => {
            const col = visible.filter((t) => t.status === st);
            return (
              <div key={st} className="bg-muted/40 rounded-sm border border-border p-3 min-h-[200px]" data-testid={`col-${st}`}>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs font-bold uppercase tracking-widest">{st}</p>
                  <span className="text-xs font-mono bg-background px-1.5 rounded-sm">{col.length}</span>
                </div>
                <div className="space-y-2">
                  {col.map((t) => {
                    const owner = isOwner(t);
                    const canDismiss = !owner && ["Completada", "Cancelada"].includes(t.status);
                    return (
                    <div key={t.id} className={`bg-card border rounded-sm p-3 cursor-pointer ${t.unread && t.assignee_id === user.id ? "border-primary ring-1 ring-primary/30" : "border-border"}`} data-testid="task-card" onClick={() => openDetail(t)}>
                      <div className="flex items-start justify-between gap-2">
                        <p className="font-semibold text-sm leading-tight flex-1">
                          {t.unread && t.assignee_id === user.id && <span className="inline-block h-2 w-2 rounded-full bg-primary mr-1.5 align-middle" />}
                          {t.title}
                        </p>
                        <Flag className={`h-3.5 w-3.5 shrink-0 ${PRIORITY_COLOR[t.priority] || ""}`} />
                      </div>
                      {t.description && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{t.description}</p>}
                      <div className="flex items-center gap-1.5 mt-2 text-[11px] text-muted-foreground">
                        <User className="h-3 w-3" /> <span className="truncate">{t.assignee_name || "—"}</span>
                        {t.activities_total > 0 && <span className="flex items-center gap-0.5 ml-1"><ListChecks className="h-3 w-3" />{t.activities_done}/{t.activities_total}</span>}
                        {t.comments_count > 0 && <span className="flex items-center gap-0.5 ml-1"><MessageSquare className="h-3 w-3" />{t.comments_count}</span>}
                        {t.due_date && <span className="ml-auto font-mono">{t.due_date}</span>}
                      </div>
                      {t.progress > 0 && <div className="h-1.5 bg-muted rounded-sm mt-2 overflow-hidden"><div className="h-full bg-primary" style={{ width: `${t.progress}%` }} /></div>}
                      <div className="flex items-center gap-1 mt-2 pt-2 border-t border-border" onClick={(e) => e.stopPropagation()}>
                        <Select value={t.status} onValueChange={(v) => move(t, v)}>
                          <SelectTrigger className="h-7 text-xs rounded-sm flex-1" data-testid={`task-status-${t.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{ALL_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                        </Select>
                        <button onClick={() => openDetail(t)} className="p-1.5 hover:bg-muted rounded-sm" data-testid="view-task"><Eye className="h-3.5 w-3.5" /></button>
                        {owner && <button onClick={() => openEdit(t)} className="p-1.5 hover:bg-muted rounded-sm" data-testid="edit-task"><Pencil className="h-3.5 w-3.5" /></button>}
                        {owner && <button onClick={() => del(t)} className="p-1.5 hover:bg-muted rounded-sm text-destructive" data-testid="delete-task"><Trash2 className="h-3.5 w-3.5" /></button>}
                        {canDismiss && <button onClick={() => dismiss(t)} className="p-1.5 hover:bg-muted rounded-sm text-muted-foreground" title="Quitar de mi muro" data-testid="dismiss-task"><X className="h-3.5 w-3.5" /></button>}
                      </div>
                    </div>
                  );})}
                  {col.length === 0 && <p className="text-xs text-muted-foreground text-center py-4">Vacío</p>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <TaskFormDialog open={open} setOpen={setOpen} editing={editing} form={form} setForm={setForm} users={users} save={save} />

      <TaskDetailDialog detail={detail} setDetail={setDetail} users={users} user={user} isOwner={isOwner}
        onSync={syncTask} onEdit={(t) => { setDetail(null); openEdit(t); }} onDelete={del} onDismiss={dismiss} onStatus={move} />
    </div>
  );
}

const TaskFormDialog = ({ open, setOpen, editing, form, setForm, users, save }) => {
  const addAct = () => setForm((f) => ({ ...f, activities: [...(f.activities || []), { title: "", status: "Pendiente" }] }));
  const setAct = (i, title) => setForm((f) => ({ ...f, activities: f.activities.map((a, j) => (j === i ? { ...a, title } : a)) }));
  const rmAct = (i) => setForm((f) => ({ ...f, activities: f.activities.filter((_, j) => j !== i) }));
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="rounded-sm max-w-lg" data-testid="task-dialog">
        <DialogHeader><DialogTitle className="font-heading">{editing ? "Editar tarea" : "Nueva tarea"}</DialogTitle>
          <DialogDescription>Define responsable, actividades y fechas. Solo tú (quien asigna) podrás editarla luego.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <Input placeholder="Título" className="rounded-sm" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="task-title" />
          <Textarea placeholder="Descripción" className="rounded-sm" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="task-desc" />
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-widest">Responsable</Label>
              <Select value={form.assignee_id} onValueChange={(v) => setForm({ ...form, assignee_id: v })}>
                <SelectTrigger className="rounded-sm" data-testid="task-assignee"><SelectValue /></SelectTrigger>
                <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-widest">Prioridad</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger className="rounded-sm" data-testid="task-priority"><SelectValue /></SelectTrigger>
                <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-widest">Vencimiento</Label>
              <Input type="date" className="rounded-sm" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="task-due" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-widest flex items-center gap-1"><Bell className="h-3 w-3" /> Recordatorio</Label>
              <Input type="date" className="rounded-sm" value={form.reminder} onChange={(e) => setForm({ ...form, reminder: e.target.value })} data-testid="task-reminder" />
            </div>
          </div>
          <div className="space-y-2 pt-1">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-bold uppercase tracking-widest flex items-center gap-1"><ListChecks className="h-3.5 w-3.5" /> Actividades (checklist)</Label>
              <Button type="button" size="sm" variant="outline" className="rounded-sm h-7 text-xs gap-1" onClick={addAct} data-testid="add-activity"><Plus className="h-3 w-3" /> Agregar</Button>
            </div>
            {(form.activities || []).map((a, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`activity-row-${i}`}>
                <CheckSquare className="h-4 w-4 text-muted-foreground shrink-0" />
                <Input placeholder={`Actividad ${i + 1}`} className="rounded-sm h-8 text-sm" value={a.title} onChange={(e) => setAct(i, e.target.value)} data-testid={`activity-input-${i}`} />
                <button type="button" onClick={() => rmAct(i)} className="p-1 text-destructive hover:bg-muted rounded-sm shrink-0"><X className="h-4 w-4" /></button>
              </div>
            ))}
            {(form.activities || []).length === 0 && <p className="text-xs text-muted-foreground">Sin actividades. Puedes agregar pasos que el responsable irá completando.</p>}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancelar</Button>
          <Button className="rounded-sm font-bold" onClick={save} disabled={!form.title} data-testid="save-task-btn">Guardar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const CommentThread = ({ comments, user, onSend, testid }) => {
  const [text, setText] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const files = Array.from(e.target.files || []); if (!files.length) return;
    setBusy(true);
    try { for (const f of files) { const fd = new FormData(); fd.append("file", f); const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); setAttachments((p) => [...p, data]); } }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); e.target.value = ""; }
  };
  const send = async () => {
    if (!text.trim() && !attachments.length) return; setBusy(true);
    try { await onSend(text, attachments); setText(""); setAttachments([]); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div>
      <div className="space-y-2 max-h-52 overflow-y-auto pr-1" data-testid={testid}>
        {(comments || []).length === 0 && <p className="text-xs text-muted-foreground text-center py-3">Sin comentarios aún.</p>}
        {(comments || []).map((c) => {
          const mine = c.user_id === user.id;
          return (
            <div key={c.id} className={`flex gap-2 ${mine ? "flex-row-reverse" : ""}`}>
              <div className="h-6 w-6 rounded-full bg-primary/15 text-primary text-[9px] font-bold flex items-center justify-center shrink-0">{initials(c.user_name)}</div>
              <div className={`max-w-[80%] flex flex-col ${mine ? "items-end" : ""}`}>
                <div className={`rounded-lg px-2.5 py-1.5 text-sm ${mine ? "bg-primary text-primary-foreground" : "bg-card border border-border"}`}>
                  {!mine && <p className="text-[10px] font-bold opacity-70 mb-0.5">{c.user_name}</p>}
                  {c.text && <p className="whitespace-pre-wrap break-words">{c.text}</p>}
                  {(c.attachments || []).length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-1.5">
                      {c.attachments.map((a) => a.is_image
                        ? <a key={a.id} href={fileUrl(a.id)} target="_blank" rel="noreferrer"><img src={fileUrl(a.id)} alt={a.original_filename} className="h-16 w-16 object-cover rounded-sm border border-border" /></a>
                        : <a key={a.id} href={fileUrl(a.id)} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs underline"><Paperclip className="h-3 w-3" />{a.original_filename}</a>)}
                    </div>
                  )}
                </div>
                <span className="text-[10px] text-muted-foreground mt-0.5 px-1">{fmtTime(c.created_at)}</span>
              </div>
            </div>
          );
        })}
      </div>
      {attachments.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {attachments.map((a, i) => (
            <div key={a.id} className="flex items-center gap-1 bg-card border border-border rounded-sm px-2 py-1 text-xs">
              <Paperclip className="h-3 w-3" /><span className="truncate max-w-[100px]">{a.original_filename}</span>
              <button onClick={() => setAttachments((p) => p.filter((_, j) => j !== i))}><X className="h-3 w-3 text-destructive" /></button>
            </div>
          ))}
        </div>
      )}
      <div className="flex items-end gap-2 mt-2">
        <label className="p-2 rounded-sm border border-border hover:bg-muted cursor-pointer shrink-0"><input type="file" multiple className="hidden" onChange={upload} />{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}</label>
        <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Escribe una respuesta…" rows={1} className="rounded-sm resize-none min-h-[38px]" data-testid={`${testid}-input`}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} />
        <Button className="rounded-sm shrink-0" onClick={send} disabled={busy || (!text.trim() && !attachments.length)} data-testid={`${testid}-send`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}</Button>
      </div>
    </div>
  );
};

const ActivityItem = ({ activity, taskId, user, onSync }) => {
  const [expanded, setExpanded] = useState(false);
  const [prog, setProg] = useState(activity.progress || 0);
  useEffect(() => { setProg(activity.progress || 0); }, [activity.progress]);
  const setStatus = async (status) => {
    try { const { data } = await api.post(`/tasks/${taskId}/activities/${activity.id}/status`, { status }); onSync(data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const commitProgress = async () => {
    if (prog === (activity.progress || 0)) return;
    try { const { data } = await api.post(`/tasks/${taskId}/activities/${activity.id}/progress`, { progress: prog }); onSync(data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const send = async (text, attachments) => { const { data } = await api.post(`/tasks/${taskId}/activities/${activity.id}/comments`, { text, attachments }); onSync(data); };
  const done = activity.status === "Completada";
  const cc = (activity.comments || []).length;
  return (
    <div className="border border-border rounded-sm bg-card" data-testid="activity-item">
      <div className="p-2.5 space-y-2">
        <div className="flex items-center gap-2">
          <button onClick={() => setExpanded((x) => !x)} className="p-0.5 shrink-0">{expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</button>
          <CheckSquare className={`h-4 w-4 shrink-0 ${done ? "text-emerald-600" : "text-muted-foreground"}`} />
          <span className={`flex-1 text-sm ${done ? "line-through text-muted-foreground" : ""}`}>{activity.title}</span>
          <button onClick={() => setExpanded(true)} className="flex items-center gap-0.5 text-[11px] text-muted-foreground px-1.5 py-0.5 rounded-sm hover:bg-muted shrink-0" data-testid="activity-comment-btn"><MessageSquare className="h-3.5 w-3.5" />{cc > 0 ? cc : ""}</button>
          <Select value={activity.status} onValueChange={setStatus}>
            <SelectTrigger className="h-7 w-28 text-xs rounded-sm shrink-0" data-testid="activity-status"><SelectValue /></SelectTrigger>
            <SelectContent>{ACT_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2 pl-7">
          <input type="range" min="0" max="100" step="5" value={prog}
            onChange={(e) => setProg(Number(e.target.value))}
            onMouseUp={commitProgress} onTouchEnd={commitProgress} onKeyUp={commitProgress}
            className="flex-1 h-1.5 accent-primary cursor-pointer" data-testid="activity-progress" />
          <span className="text-xs font-mono w-10 text-right text-muted-foreground">{prog}%</span>
        </div>
      </div>
      {expanded && (
        <div className="px-3 pb-3 pt-1 border-t border-border">
          <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2">Seguimiento / observaciones</p>
          <CommentThread comments={activity.comments} user={user} onSend={send} testid={`activity-comments-${activity.id}`} />
        </div>
      )}
    </div>
  );
};

const TaskDetailDialog = ({ detail, setDetail, users, user, isOwner, onSync, onEdit, onDelete, onDismiss, onStatus }) => {
  if (!detail) return null;
  const owner = isOwner(detail);
  const canDismiss = !owner && ["Completada", "Cancelada"].includes(detail.status);
  const sendGeneral = async (text, attachments) => { const { data } = await api.post(`/tasks/${detail.id}/comments`, { text, attachments }); onSync(data); };
  const reassign = async (assignee_id) => {
    try { const { data } = await api.put(`/tasks/${detail.id}`, { ...detail, assignee_id }); onSync(data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
      <DialogContent className="rounded-sm max-w-2xl p-0" data-testid="task-detail-dialog">
        <div className="p-5 border-b border-border">
          <DialogHeader>
            <div className="flex items-start justify-between gap-3 pr-6">
              <DialogTitle className="font-heading text-lg leading-tight flex items-center gap-2">
                <Flag className={`h-4 w-4 ${PRIORITY_COLOR[detail.priority] || ""}`} /> {detail.title}
              </DialogTitle>
              <span className={`text-[11px] font-bold px-2 py-1 rounded-sm shrink-0 ${STATUS_BADGE[detail.status] || "bg-muted"}`}>{detail.status}</span>
            </div>
            <DialogDescription className="sr-only">Detalle de la tarea, actividades y conversación</DialogDescription>
          </DialogHeader>
          {detail.description && <p className="text-sm text-muted-foreground mt-2 whitespace-pre-wrap">{detail.description}</p>}

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 text-xs">
            <div><p className="text-muted-foreground uppercase tracking-widest text-[10px] font-bold">Asignó</p><p className="font-medium mt-0.5">{detail.owner_name}</p></div>
            <div><p className="text-muted-foreground uppercase tracking-widest text-[10px] font-bold">Responsable</p><p className="font-medium mt-0.5">{detail.assignee_name}</p></div>
            {detail.due_date && <div><p className="text-muted-foreground uppercase tracking-widest text-[10px] font-bold">Vence</p><p className="font-medium mt-0.5 font-mono">{detail.due_date}</p></div>}
            <div><p className="text-muted-foreground uppercase tracking-widest text-[10px] font-bold">Avance</p><p className="font-medium mt-0.5">{detail.progress || 0}%</p></div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4 pt-4 border-t border-border">
            <div className="space-y-1.5">
              <Label className="text-[10px] font-bold uppercase tracking-widest">Estado de la tarea</Label>
              <Select value={detail.status} onValueChange={(v) => onStatus(detail, v)}>
                <SelectTrigger className="rounded-sm h-9" data-testid="detail-status"><SelectValue /></SelectTrigger>
                <SelectContent>{ALL_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {owner && (
              <div className="space-y-1.5">
                <Label className="text-[10px] font-bold uppercase tracking-widest">Reasignar a</Label>
                <Select value={detail.assignee_id} onValueChange={reassign}>
                  <SelectTrigger className="rounded-sm h-9" data-testid="detail-reassign"><SelectValue /></SelectTrigger>
                  <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 mt-3">
            {owner && <Button variant="outline" size="sm" className="rounded-sm gap-1.5 text-xs" onClick={() => onEdit(detail)} data-testid="detail-edit"><Pencil className="h-3.5 w-3.5" /> Editar</Button>}
            {owner && <Button variant="outline" size="sm" className="rounded-sm gap-1.5 text-xs text-destructive" onClick={() => onDelete(detail)} data-testid="detail-delete"><Trash2 className="h-3.5 w-3.5" /> Eliminar</Button>}
            {canDismiss && <Button variant="outline" size="sm" className="rounded-sm gap-1.5 text-xs" onClick={() => onDismiss(detail)} data-testid="detail-dismiss"><X className="h-3.5 w-3.5" /> Quitar de mi muro</Button>}
            {!owner && !canDismiss && <p className="text-[11px] text-muted-foreground">Solo quien asignó la tarea puede editarla o eliminarla.</p>}
          </div>
        </div>

        <div className="p-5 max-h-[45vh] overflow-y-auto space-y-5">
          {(detail.activities || []).length > 0 && (
            <div>
              <p className="text-xs font-bold uppercase tracking-widest mb-3 flex items-center gap-1.5"><ListChecks className="h-3.5 w-3.5" /> Actividades ({detail.activities_done}/{detail.activities_total})</p>
              <div className="space-y-2" data-testid="activities-list">
                {detail.activities.map((a) => <ActivityItem key={a.id} activity={a} taskId={detail.id} user={user} onSync={onSync} />)}
              </div>
            </div>
          )}
          <div>
            <p className="text-xs font-bold uppercase tracking-widest mb-3 flex items-center gap-1.5"><MessageSquare className="h-3.5 w-3.5" /> Comentarios generales</p>
            <CommentThread comments={detail.comments} user={user} onSend={sendGeneral} testid="task-comments" />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
